import { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import { supabase } from '../../supabaseClient';

const blank = { name: '', weight_oz_max: '', length_in: '', width_in: '', height_in: '' };

export default function AdminShippingBoxesPage() {
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blank);
  const [error, setError] = useState('');
  // Inline edit state: which box is being edited, and its working values.
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(blank);
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    const { data, error: err } = await supabase.from('shipping_boxes').select('*').order('weight_oz_max');
    if (err) setError(err.message);
    setBoxes(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addBox = async (e) => {
    e.preventDefault();
    const { error: err } = await supabase.from('shipping_boxes').insert({
      name: form.name.trim(),
      weight_oz_max: parseFloat(form.weight_oz_max),
      length_in: parseFloat(form.length_in),
      width_in: parseFloat(form.width_in),
      height_in: parseFloat(form.height_in),
    });
    if (err) { setError(err.message); return; }
    setForm(blank);
    setError('');
    load();
  };

  const startEdit = (box) => {
    setError('');
    setEditingId(box.id);
    setEditForm({
      name: box.name ?? '',
      weight_oz_max: box.weight_oz_max ?? '',
      length_in: box.length_in ?? '',
      width_in: box.width_in ?? '',
      height_in: box.height_in ?? '',
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditForm(blank); };

  const saveEdit = async (id) => {
    setSavingEdit(true);
    const { error: err } = await supabase.from('shipping_boxes').update({
      name: editForm.name.trim(),
      weight_oz_max: parseFloat(editForm.weight_oz_max),
      length_in: parseFloat(editForm.length_in),
      width_in: parseFloat(editForm.width_in),
      height_in: parseFloat(editForm.height_in),
    }).eq('id', id);
    setSavingEdit(false);
    if (err) { setError(err.message); return; }
    setError('');
    cancelEdit();
    load();
  };

  const toggleActive = async (box) => {
    await supabase.from('shipping_boxes').update({ active: !box.active }).eq('id', box.id);
    load();
  };

  const removeBox = async (id) => {
    await supabase.from('shipping_boxes').delete().eq('id', id);
    load();
  };

  if (loading) return <div>Loading…</div>;

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: 6 }}>Shipping boxes</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 20 }}>
        Packaging options the checkout rate-shopper picks from — smallest box whose max weight fits the cart.
      </p>

      <form onSubmit={addBox} style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap', maxWidth: 700 }}>
        <input className="sv-input" style={{ width: 160 }} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <input className="sv-input" style={{ width: 110 }} placeholder="Max oz" type="number" value={form.weight_oz_max} onChange={(e) => setForm({ ...form, weight_oz_max: e.target.value })} required />
        <input className="sv-input" style={{ width: 90 }} placeholder="L in" type="number" value={form.length_in} onChange={(e) => setForm({ ...form, length_in: e.target.value })} required />
        <input className="sv-input" style={{ width: 90 }} placeholder="W in" type="number" value={form.width_in} onChange={(e) => setForm({ ...form, width_in: e.target.value })} required />
        <input className="sv-input" style={{ width: 90 }} placeholder="H in" type="number" value={form.height_in} onChange={(e) => setForm({ ...form, height_in: e.target.value })} required />
        <button type="submit" className="sv-btn"><Plus size={16} /> Add</button>
      </form>

      {error && <div style={{ color: 'var(--danger)', marginBottom: 16, fontSize: '0.85rem' }}>{error}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 700 }}>
        {boxes.map((b) => (
          editingId === b.id ? (
            <div key={b.id} className="sv-card" style={{ padding: '12px 16px' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input className="sv-input" style={{ width: 160 }} placeholder="Name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                <input className="sv-input" style={{ width: 110 }} placeholder="Max oz" type="number" value={editForm.weight_oz_max} onChange={(e) => setEditForm({ ...editForm, weight_oz_max: e.target.value })} />
                <input className="sv-input" style={{ width: 90 }} placeholder="L in" type="number" value={editForm.length_in} onChange={(e) => setEditForm({ ...editForm, length_in: e.target.value })} />
                <input className="sv-input" style={{ width: 90 }} placeholder="W in" type="number" value={editForm.width_in} onChange={(e) => setEditForm({ ...editForm, width_in: e.target.value })} />
                <input className="sv-input" style={{ width: 90 }} placeholder="H in" type="number" value={editForm.height_in} onChange={(e) => setEditForm({ ...editForm, height_in: e.target.value })} />
                <button onClick={() => saveEdit(b.id)} disabled={savingEdit} className="sv-btn" style={{ padding: '8px 12px', fontSize: '0.8rem' }}>
                  <Check size={15} /> {savingEdit ? 'Saving…' : 'Save'}
                </button>
                <button onClick={cancelEdit} disabled={savingEdit} className="sv-btn-outline" style={{ padding: 8, borderRadius: 999 }}>
                  <X size={15} />
                </button>
              </div>
            </div>
          ) : (
            <div key={b.id} className="sv-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', opacity: b.active ? 1 : 0.5 }}>
              <div>
                <div style={{ fontWeight: 700 }}>{b.name}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  up to {b.weight_oz_max} oz · {b.length_in}×{b.width_in}×{b.height_in} in
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => startEdit(b)} className="sv-btn-outline" style={{ padding: '6px 12px', fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <Pencil size={14} /> Edit
                </button>
                <button onClick={() => toggleActive(b)} className="sv-btn-outline" style={{ padding: '6px 12px', fontSize: '0.8rem' }}>
                  {b.active ? 'Deactivate' : 'Activate'}
                </button>
                <button onClick={() => removeBox(b.id)} className="sv-btn-outline" style={{ padding: 8, borderRadius: 999 }}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}
